<template>
  <Content
    :title="$t('AGENTS.TITLE')"
    :hint="$t('HINTS.SETTING.AGENTS')"
    :description="$t('AGENTS.AGENTS_INFO_DESC')"
  >
    <ElButton
      native-type="button"
      type="primary"
      icon="el-icon-plus"
      @click.native.prevent="openModal('newModalVisible')"
    >
      {{ $t('AGENTS.NEW_AGENT_BTN') }}
    </ElButton>

    <CardApiError
      v-if="apiError"
      :error-text="$t('AGENTS.API_ERROR')"
      @retry="listAgents"
    />

    <div
      v-else-if="agentsList.length || pendingAgents.length"
      class="table-responsive raised"
    >
      <table class="table table--padded table--hover">
        <thead>
          <tr>
            <th>{{ $t('AGENTS.AGENT_TITLE') }}</th>
            <th>{{ $t('EMAIL') }}</th>
            <th />
            <th />
          </tr>
        </thead>
        <tbody>
          <tr v-for="agent in pendingAgents" :key="agent.email">
            <td v-if="agent.title">
              {{ agent.title }}
            </td>
            <td v-else class="text-disabled">
              {{ $t('UNTITLED') }}
            </td>

            <td colspan="2" class="text-latin">
              {{ agent.email }}
              <span class="chip chip--mini chip--warning">
                {{ $t('AGENTS.PENDING_INVITATION') }}
              </span>
            </td>
            <td />
          </tr>
          <tr v-for="agent in agentsList" :key="agent.email">
            <td v-if="agent.title">
              {{ agent.title }}
            </td>
            <td v-else class="text-disabled">
              {{ $t('UNTITLED') }}
            </td>

            <td class="text-latin">
              {{ agent.email }}
            </td>
            <td>
              <ElButton
                icon="el-icon-edit"
                :plain="true"
                size="small"
                @click.native="handlePermissionsModal(agent)"
              >
                {{ $t('AGENTS.EDIT_PERMISSIONS_BTN') }}
              </ElButton>
            </td>
            <td class="text-end">
              <ElButton
                type="danger"
                icon="el-icon-delete"
                size="small"
                @click.native.prevent="deleteAgentWithConfirm(agent.id)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="card">
      <div
        v-show="isLoadingAgents"
        class="card__block card__block--loading"
        dir="ltr"
      >
        <HollowDotsSpinner
          :animation-duration="1000"
          :dot-size="12"
          :dots-num="3"
          :color="'#95a5a6'"
        />
      </div>

      <div v-show="!isLoadingAgents" class="card__block">
        {{ $t('AGENTS.NO_DATA') }}
      </div>
    </div>

    <AgentPermissionsModalWrapper
      v-if="currentAgent"
      v-model="permissionsModalVisible"
      :agent="currentAgent"
      @updateAgentPermissions="updateCurrentAgentPermissions"
    />

    <AddAgentModal v-model="newModalVisible" @add="addAgent" />
  </Content>
</template>

<script>
import HollowDotsSpinner from '@@/common/HollowDotsSpinner';

import { Agents } from '@/api/api';

import modal from '@/mixins/modal';
import layout from '@/mixins/layout';

import AddAgentModal from './modal/AddAgentModal';
import AgentPermissionsModalWrapper from './AgentPermissionsModalWrapper';

import CardApiError from '@@/common/CardApiError';
import Content from '@@/common/layout/Content';

export default {
  name: 'Agents',

  components: {
    AddAgentModal,
    AgentPermissionsModalWrapper,
    HollowDotsSpinner,
    CardApiError,
    Content,
  },

  mixins: [modal, layout],

  data() {
    return {
      pendingAgents: [],
      acceptedAgents: [],
      currentAgent: null,
      newModalVisible: false,
      permissionsModalVisible: false,

      isLoadingAgents: false,
      apiError: false,
    };
  },

  computed: {
    /**
     * agentsList
     *
     * filters the agents to ignore the admin agent
     * belonging to the publisher account him/herself
     *
     * @returns {Array.<T>}
     */
    agentsList() {
      return this.acceptedAgents.filter(agent => !agent.is_admin);
    },
  },

  created() {
    this.setPageTitle('SETTINGS');

    // list the agents
    this.listAgents();
  },

  methods: {
    updateCurrentAgentPermissions(newAgentPermissions) {
      this.currentAgent.permissions = newAgentPermissions;
    },
    listAgents() {
      this.isLoadingAgents = true;
      this.apiError = false;
      return Agents.getAgents()
        .then(({ body: { agents, invited_agents: pendingAgents = [] } }) => {
          this.isLoadingAgents = false;
          this.pendingAgents = pendingAgents;
          this.acceptedAgents = agents;
        })
        .catch(({ body: { message } = {} }) => {
          this.isLoadingAgents = false;

          if (message) {
            this.$message.error(message);
          }

          this.apiError = true;
        });
    },

    addAgent(agent) {
      this.pendingAgents.unshift(agent);
    },

    deleteAgentWithConfirm(id) {
      return this.deletionConfirm()
        .then(() => {
          this.deleteAgent(id);
        })
        .catch(() => {});
    },

    deleteAgent(id) {
      return Agents.delete({ id })
        .then(() => {
          this.acceptedAgents = this.removeById(this.acceptedAgents, id);
          this.$message.success(this.$t('DELETE_COMPLETED'));
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        });
    },

    setCurrentAgent(agent) {
      this.currentAgent = agent;
    },

    handlePermissionsModal(agent) {
      this.setCurrentAgent(agent);
      this.openModal('permissionsModalVisible');
    },

    removeById(list, id) {
      const arr = Array.from(list);
      arr.splice(
        arr.findIndex(agent => agent.id === id),
        1,
      );
      return arr;
    },

    deletionConfirm() {
      return this.$confirm(this.$t('DELETE_ITEM_WARNING'), this.$t('WARNING'), {
        confirmButtonText: this.$t('DELETE_IT'),
        cancelButtonText: this.$t('CANCEL'),
        cancelButtonClass: 'el-button--text is-plain',
        type: 'warning',
      });
    },
  },
};
</script>
